/**
 * The request never got an answer: offline, DNS, refused connection, CORS at
 * the network layer, or a timeout. Callers may fall back to local data.
 */
export class ConnectivityError extends Error {
  readonly cause: unknown;

  constructor(message = 'The server could not be reached', options?: { cause?: unknown }) {
    super(message);
    this.name = 'ConnectivityError';
    this.cause = options?.cause;
  }
}

/** The server answered with an error status. Callers should not hide it behind "offline". */
export class HttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'HttpError';
  }
}

export const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

/**
 * fetch with a timeout, turning failures into `ConnectivityError` (no answer)
 * or `HttpError` (an error status). A 2xx body that isn't JSON is an HttpError;
 * a 204 resolves to undefined.
 */
export async function fetchJson<T>(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit = {},
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  // One deadline for the headers and the body, so a stalled body times out too.
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let res: Response;
    try {
      res = await fetchImpl(url, { ...init, signal: controller.signal });
    } catch (err) {
      throw new ConnectivityError(controller.signal.aborted ? 'The request timed out' : 'The server could not be reached', { cause: err });
    }
    if (!res.ok) {
      let message = `Request failed: ${res.status}`;
      try {
        const body = (await res.json()) as { error?: unknown };
        if (typeof body?.error === 'string') message = body.error;
      } catch {
        // Not JSON (or cut off); keep the status message.
      }
      throw new HttpError(res.status, message);
    }
    // No Content: nothing to parse (sign out, deletes).
    if (res.status === 204) return undefined as T;
    try {
      return (await res.json()) as T;
    } catch (err) {
      if (controller.signal.aborted) throw new ConnectivityError('The request timed out', { cause: err });
      throw new HttpError(res.status, 'The server sent an unreadable response');
    }
  } finally {
    clearTimeout(timer);
  }
}

export function isConnectivityError(err: unknown): err is ConnectivityError {
  return err instanceof ConnectivityError;
}

/** Request options for a JSON API call: the body as JSON, and the sign-in token when there is one. */
export function jsonRequest(options: { method?: string; body?: unknown; token?: string | null } = {}): RequestInit {
  const { method, body, token } = options;
  return {
    ...(method ? { method } : {}),
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
}
